import type { UseQueryOptions } from '@tanstack/react-query';

import { useQuery } from '@tanstack/react-query';

import { assignedRiskApplicationService } from '../services/assigned-risk-application.service';

import type { ApiResponse, ErrorResponse } from '../services/api.types';
import type { AssignedRiskApplicationAgentViewModel } from '../types/AssignedRiskApplicationAgentViewModel';
import type { AssignedRiskNamesAndAddressesViewModel } from '../types/AssignedRiskNamesAndAddressesViewModel';
import type { AssignedRiskPreviousInsuranceViewModel } from '../types/AssignedRiskPreviousInsuranceViewModel';
import type { AssignedRiskApplicationDetailsViewModel } from '../types/AssignedRiskApplicationDetailsViewModel';
import type { AssignedRiskApplicationPageContextViewModel } from '../types/AssignedRiskApplicationPageContextViewModel';
import type { AssignedRiskApplicationCoverageInformationViewModel } from '../types/AssignedRiskApplicationCoverageInformationViewModel';

export const useAssignedRiskApplicationContext = (
  assignedRiskAppId: number,
  options?: UseQueryOptions<ApiResponse<AssignedRiskApplicationPageContextViewModel>, ErrorResponse>
) =>
  useQuery<ApiResponse<AssignedRiskApplicationPageContextViewModel>, ErrorResponse>({
    queryKey: ['assignedRiskApplicationContext', assignedRiskAppId],
    queryFn: () => assignedRiskApplicationService.getContext(assignedRiskAppId),
    enabled: !!assignedRiskAppId,
    ...options,
  });

export const useAssignedRiskApplicationAgentDetails = (
  assignedRiskAppId: number,
  options?: UseQueryOptions<ApiResponse<AssignedRiskApplicationAgentViewModel>, ErrorResponse>
) =>
  useQuery<ApiResponse<AssignedRiskApplicationAgentViewModel>, ErrorResponse>({
    queryKey: ['assignedRiskApplicationAgentDetails', assignedRiskAppId],
    queryFn: () => assignedRiskApplicationService.getAgentDetails(assignedRiskAppId),
    enabled: !!assignedRiskAppId,
    ...options,
  });

export const useAssignedRiskApplicationApplicationDetails = (
  assignedRiskAppId: number,
  options?: UseQueryOptions<ApiResponse<AssignedRiskApplicationDetailsViewModel>, ErrorResponse>
) =>
  useQuery<ApiResponse<AssignedRiskApplicationDetailsViewModel>, ErrorResponse>({
    queryKey: ['assignedRiskApplicationApplicationDetails', assignedRiskAppId],
    queryFn: () => assignedRiskApplicationService.getApplicationDetails(assignedRiskAppId),
    enabled: !!assignedRiskAppId,
    ...options,
  });

export const useAssignedRiskApplicationInsuranceHistory = (
  assignedRiskAppId: number,
  options?: UseQueryOptions<ApiResponse<AssignedRiskPreviousInsuranceViewModel[]>, ErrorResponse>
) =>
  useQuery<ApiResponse<AssignedRiskPreviousInsuranceViewModel[]>, ErrorResponse>({
    queryKey: ['assignedRiskApplicationInsuranceHistory', assignedRiskAppId],
    queryFn: () => assignedRiskApplicationService.getInsuranceHistory(assignedRiskAppId),
    enabled: !!assignedRiskAppId,
    ...options,
  });

export const useAssignedRiskApplicationNamesAndAddresses = (
  assignedRiskAppId: number,
  options?: UseQueryOptions<ApiResponse<AssignedRiskNamesAndAddressesViewModel>, ErrorResponse>
) =>
  useQuery<ApiResponse<AssignedRiskNamesAndAddressesViewModel>, ErrorResponse>({
    queryKey: ['assignedRiskApplicationNamesAndAddresses', assignedRiskAppId],
    queryFn: () => assignedRiskApplicationService.getNamesAndAddresses(assignedRiskAppId),
    enabled: !!assignedRiskAppId,
    ...options,
  });

export const useAssignedRiskApplicationCoverageInformation = (
  assignedRiskAppId: number,
  options?: UseQueryOptions<ApiResponse<AssignedRiskApplicationCoverageInformationViewModel>, ErrorResponse>
) =>
  useQuery<ApiResponse<AssignedRiskApplicationCoverageInformationViewModel>, ErrorResponse>({
    queryKey: ['assignedRiskApplicationCoverageInformation', assignedRiskAppId],
    queryFn: () => assignedRiskApplicationService.getCoverageInformation(assignedRiskAppId),
    enabled: !!assignedRiskAppId,
    ...options,
  });